/**
 * Auth Context
 *
 * 使用 Line ID 進行登入
 * 在此判斷會員是否已註冊過
 * 若是已註冊，API會回傳 access Token
 * 若尚未註冊，則會吐回404
 */
import * as PropTypes from 'prop-types';
import {
  Consumer,
  Context,
  createContext,
  FC,
  useContext,
  useEffect,
  useState,
  ReactNode
} from 'react';
import { useNavigate, useLocation  } from 'react-router';
import * as Sentry from '@sentry/browser';
import liff from '@line/liff';
import { loginMgmtByLine } from '../../api/mgmt';
import { getMe, MeData } from '../../api/me';
// import { useLiff } from '../Liff';
// 22.04.19 SCH, Add storage4user['meUserStorage']
import { GlobalContext4User } from '../../storage/user';

interface AuthProviderProps {
  lineToken: string;
  children:ReactNode;
}
interface AuthContext {
  error?: any;
  isLoggedIn: boolean;
  logging: boolean;
  isMember: boolean;
  isNormal: boolean;
  accessToken: string | null | undefined;
  auth: (redirect?: string) => void;
  fetching: boolean;
  me?: MeData;
  queryMe?: () => Promise<void>;
}
type CreateAuthContext = () => {
  AuthConsumer: Consumer<AuthContext>;
  AuthProvider: FC<AuthProviderProps>;
  useAuth: () => AuthContext;
};
const AuthProviderPropTypes = {
  children: PropTypes.element.isRequired,
  lineToken: PropTypes.string.isRequired,
  // brandID: PropTypes.number.isRequired,
};

const createAuthProvider = (context: Context<AuthContext>) => {
  const AuthProvider: FC<AuthProviderProps> = ({ children, lineToken }) => {
    const [error, setError] = useState<any>();
    const [logging, setLogging] = useState<boolean>(true);
    const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
    const [isMember, setIsMember] = useState<boolean>(false);
    const [isNormal, setIsNormal] = useState<boolean>(false);
    const [accessToken, setAccessToken] = useState<string>();
    const [me, setMe] = useState<MeData>();
    const [fetching, setFetching] = useState<boolean>(false);
    const navigate = useNavigate();
    const location = useLocation();
    // const { liff } = useLiff();
    // @ts-ignore // 22.04.19 SCH
    const { meUserStorage } = useContext(GlobalContext4User);
    const { setToken, setUser } = meUserStorage; // 22.04.19 SCH
    const { setLinePay } = meUserStorage; // 22.11.02 SCH
    const { setUserRole } = meUserStorage; // 22.11.23 SCH
    const { setUserType } = meUserStorage; // 22.11.23 SCH
    

    const auth = async (redirect?: string) => {
      try {
        // PG-55 外循環:登入改走新版後端(management-backend)。同一顆 LINE idToken
        // 換 Sanctum token,accessToken 即新版 token,全站 member 端點統一走新版。
        let result: { token: string; member: any } | null = null;
        try {
          result = await loginMgmtByLine(lineToken);
        } catch (loginError: any) {
          const status = loginError?.response?.status;
          // LINE idToken 過期(新後端回 401)→ 重新 LIFF 登入拿新 token
          if (status === 401) {
            try {
              if (liff.isLoggedIn && liff.isLoggedIn()) {
                liff.logout();
              }
              liff.login({ redirectUri: window.location.href });
              return;
            } catch (e) {
              console.log('liff re-login failed', e);
            }
          }
          setError(loginError);
          setIsLoggedIn(false);
          return;
        }

        if (!result || !result.member) {
          setIsLoggedIn(false);
          setIsMember(false);
          return;
        }

        const { token, member } = result;

        // 阻擋沒有手機號碼(對齊舊版)
        if (!member.mobile_number) {
          console.log('no mobile_phone_number');
          setIsLoggedIn(false);
          setIsMember(false);
          return;
        }

        setAccessToken(token);
        setToken(token); // 22.04.19 SCH
        setIsLoggedIn(true);
        setIsNormal(true); // 新後端無「會員等級」概念,預設一般會員
        setIsMember(true);

        queryMe(token);

        if (redirect) {
          navigate('/');
        }
      } catch (error) {
        setError(error);
        setIsLoggedIn(false);
        setIsMember(false);
        setIsNormal(false);
      }
    };
    
    const queryMe = async (token: string) => {
      try {
        setFetching(true);
        const resp = await getMe(token);
        Sentry.setUser({
          username: `${resp.data?.realname}`,
          id: `${resp.data?.id}`,
          lineId: `${resp.data?.line_id}`,
        });
        // console.log(resp);
        setMe(resp.data);
        setFetching(false);
        setUser(resp.data); // 22.04.19 SCH
        setLinePay(resp.data?.line_pay); // 22.11.02 SCH
        setUserRole(resp.data?.user_role); // 22.11.02 SCH
        setUserType(resp.data?.user_type); // 22.11.02 SCH

      } catch (error) {
        console.log(error);
        setFetching(false);
      }
    };
    useEffect(() => {
      // 單一入口改版後：歸還入口頁 /rent 與掃描頁 /return/* 未登入時也允許進入
      // Liff context 不會自動 login，lineToken 可能為空，此時直接放行讓頁面渲染
      if (!lineToken) {
        const p = location.pathname;
        if (p.startsWith('/return') || p === '/rent' || p.startsWith('/rent/') || p.startsWith('/nearby')) {
          setLogging(false);
        }
        return;
      }

      (async () => {
        setLogging(true);
        await auth();
        setLogging(false);
      })();
      // eslint-disable-next-line
    }, [lineToken]);
    return (
      <context.Provider
        value={{
          error,
          logging,
          accessToken: accessToken,
          isLoggedIn,
          isMember,
          isNormal,
          auth,
          me,
          fetching,
          queryMe: async () => queryMe(accessToken!),
        }}
      >
        {children}
      </context.Provider>
    );
  };

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  AuthProvider.propTypes = AuthProviderPropTypes;
  return AuthProvider;
};
export const createAuthContext: CreateAuthContext = () => {
  // @ts-ignore
  const context = createContext<AuthContext>({
    isLoggedIn: false,
    accessToken: undefined,
    logging: true,
    isMember: false,
    isNormal: false,
    auth: () => {},
    me: undefined,
    fetching: false,
  });
  context.displayName = 'AuthContext';

  return {
    AuthConsumer: context.Consumer,
    AuthProvider: createAuthProvider(context),
    useAuth: () => useContext(context),
  };
};

export const { AuthConsumer, AuthProvider, useAuth } = createAuthContext();
